// Three.js core
import * as THREE from 'three';

// Global drawing state and constants
import { DrawingGlobals, HISTORY, LABELS, POLYGONS, VERTICES } from "../Global/variables";
import { COLORS, LINES } from "../Global/materials";

// Geometry utilities
import { createEdges, createVertex } from "./Drawing";
import { getGeoCenter, removeLineMeshes, earClipping } from "../Global/functions";

// Label and calculation utilities
import { createNameLabel } from "./Labels";
import { calculatePitch, getAzimuth, getPolyArea } from "./AreaPitchAzimu";

// Snapping tools
import { refLineSnap, showReferenceLine } from "./Snaps/ReferenceLine";
import { hideSmartGuides, updateSmartGuides } from './Snaps/GuidingLines';
import { showAngle, snap90Deg } from "./Snaps/SnapTo90";
import { showLength } from './Snaps/Showlength';
import { vertexSnap } from "./Snaps/VertexSnap";
import { getSnapPoint } from "./Snaps/Snaps";
import { lengthSnap as computeLengthSnap } from './Snaps/LengthSnap';

// Polygon selection logic
import { selectSegment } from "./Functions";

// Undo + delete + drag controls
import { pushUndoAction } from "../Global/UndoManager";
import { deleteSingleSegment } from "./Delete";
import { removeSegmentDragControls } from "./DragControls";

// SegmentManager handles drawing and editing of polygonal roof areas
class SegmentManager {
    constructor(scene, stopDrawing, updateSegmentList, selectedIndices, setSelectedIndices, setPolygonDetails) {
        this.scene = scene;
        this.stopDrawing = stopDrawing;

        // Snapping references
        this.snapVertex = null;
        this.snap90 = null;
        this.snapRefLine = null;
        this.snapPoint = null;
        this.snapLen = null;

        this.lastMouseIntersect = null;
        this.snap90Locked = false;
        this.snap90Dir = null;

        this.updateSegmentList = updateSegmentList;
        this.selectedIndices = selectedIndices;
        this.setSelectedIndices = setSelectedIndices;
        this.setPolygonDetails = setPolygonDetails;

        this.lastUndo = false;
    }

    // Called on left-click: adds a point or completes the polygon
    handleMouseDown(point) {
        if (this.snapPoint) {
            point = this.snapPoint;
            this.snapPoint = null;
            this.snapLen = null;

            if (this.snap90Locked) {
                console.log('Snap90: reset on commit');
                this.snap90Locked = false;
                this.snap90Dir = null;
            }
        }

        // Check if user closed the polygon by clicking near the first point
        if (
            DrawingGlobals.intersectionPoints.length > 2 &&
            point.distanceTo(DrawingGlobals.intersectionPoints[0]) < 0.5
        ) {
            DrawingGlobals.lineMeshes.slice(-1)[0].material.color.setHex(COLORS.red);
            this.completeShape();
        } else {
            DrawingGlobals.intersectionPoints.push(point);
            this.drawLine();
        }

        // clear local drawing HISTORY (used by drawing-time redo)
        HISTORY.length = 0;
    }

    // Handles mouse move: update preview lines and snapping
    handleMouseMove(point) {
        this.lastMouseIntersect = point;

        if (DrawingGlobals.intersectionPoints.length === 0) {
            this.snapVertex = vertexSnap(this.snapVertex, point);
            if (this.snapVertex) {
                this.snapVertex.material.opacity = 1;
                if (this.snapVertex.position.distanceTo(point) <= 0.18) {
                    this.snapPoint = this.snapVertex.position;
                } else {
                    this.snapPoint = null;
                }
            } else {
                this.snapPoint = null;
            }
        }

        // ---------------------------------------------------------------
        // 🔵 Close-polygon hover snap
        // ---------------------------------------------------------------
        if (DrawingGlobals.intersectionPoints.length > 2) {
            const first = DrawingGlobals.intersectionPoints[0];
            const hoverDist = 0.8;

            if (point.distanceTo(first) < hoverDist) {
                this.snapPoint = first.clone();
            }
        }
        // ---------------------------------------------------------------

        if (DrawingGlobals.intersectionPoints.length >= 1) {
            this.updateLine(point);
            updateSmartGuides(point);

            let lengthSnap = null;

            if (DrawingGlobals.intersectionPoints.length >= 2) {

                // Snap to reference lines and 90-degree angles
                this.snapRefLine = refLineSnap(this.snapRefLine, point);
                if (this.snapRefLine) showReferenceLine(this.snapRefLine);

                const pts = DrawingGlobals.intersectionPoints;
                const B = pts[pts.length - 1];
                const C = pts[pts.length - 2];

                const snap90Candidate = snap90Deg(this.snap90, point);

                const engageThresh = 0.18;
                const releaseThresh = 0.04;

                if (!this.snap90Locked) {
                    if (snap90Candidate && snap90Candidate.distanceTo(point) <= engageThresh) {
                        const BC = new THREE.Vector3().subVectors(C, B);
                        const BCn = BC.length() > 1e-6 ? BC.clone().normalize() : new THREE.Vector3(1, 0, 0);
                        const AB = new THREE.Vector3().subVectors(point, B);
                        const projLen = AB.dot(BCn);
                        const proj = BCn.clone().multiplyScalar(projLen);
                        let D = AB.clone().sub(proj);
                        if (D.length() < 1e-6) {
                            const up = new THREE.Vector3(0, 0, 1);
                            D = new THREE.Vector3().crossVectors(BCn, up);
                            if (D.length() < 1e-6) D = new THREE.Vector3(1, 0, 0);
                        }
                        D.normalize();
                        this.snap90Dir = D;
                        this.snap90Locked = true;
                        console.log('Snap90: engaged, locked direction', this.snap90Dir);
                    }
                } else {
                    const AB = new THREE.Vector3().subVectors(point, B);
                    const t = Math.max(0, AB.dot(this.snap90Dir));
                    const lockedPoint = B.clone().add(this.snap90Dir.clone().multiplyScalar(t));
                    this.snap90 = lockedPoint;

                    const lateral = AB.clone().sub(this.snap90Dir.clone().multiplyScalar(t)).length();
                    if (lateral > releaseThresh) {
                        console.log('Snap90: released (lateral deviation)', lateral.toFixed(3));
                        this.snap90Locked = false;
                        this.snap90Dir = null;
                    }
                }

                if (!this.snap90Locked) this.snap90 = snap90Candidate;

                if (this.snap90Locked && this.snap90Dir) {
                    const ANGLE_TOL_DEG = 2;
                    const COS_TOL = Math.cos(THREE.MathUtils.degToRad(ANGLE_TOL_DEG));
                    const SNAP_DIST_THRESHOLD = 0.03;

                    const D = this.snap90Dir.clone();

                    const edges = [];
                    for (let i = 0; i < pts.length - 1; i++) {
                        const p1 = pts[i];
                        const p2 = pts[i + 1];
                        const e = p2.clone().sub(p1);
                        const elen = e.length();
                        if (elen < 1e-6) continue;
                        const edir = e.clone().divideScalar(elen);
                        edges.push({ i, p1, p2, edir, elen });
                    }

                    let best = null;
                    for (const { i, edir, elen } of edges) {
                        const dot = Math.abs(edir.dot(D));
                        if (dot >= COS_TOL) {
                            const S = B.clone().add(D.clone().multiplyScalar(elen));
                            const base = this.snap90 ? this.snap90 : point;
                            const dWorld = S.distanceTo(base);
                            if (dWorld <= SNAP_DIST_THRESHOLD) {
                                if (!best || dWorld < best.d) {
                                    best = { i, S, d: dWorld, L: elen };
                                }
                            }
                        }
                    }

                    if (best) {
                        lengthSnap = best.S;
                        this.snapLen = lengthSnap;
                        DrawingGlobals.lengthSnapMatchedEdges = [best.i];
                        console.log(`LengthSnap(locked): edgeIndex=${best.i}`);
                    } else {
                        this.snapLen = null;
                        lengthSnap = null;
                        DrawingGlobals.lengthSnapMatchedEdges = [];
                    }
                } else {
                    this.snapLen = computeLengthSnap(this.snapLen, (this.snap90 ?? point));
                    lengthSnap = this.snapLen;
                }
            }

            this.snapVertex = vertexSnap(this.snapVertex, point);
            this.snapPoint = getSnapPoint(this.snapPoint, this.snapVertex, this.snap90, lengthSnap, point);
            showAngle(this.snapPoint);
            showLength(this.snapPoint);
            this.updateLine(this.snapPoint);
        }
    }

    // Create a new line segment
    drawLine() {
        const point = DrawingGlobals.intersectionPoints.slice(-1)[0];
        const geometry = new THREE.BufferGeometry().setFromPoints([point, point]);

        const material = LINES.drawingLineMaterial.clone();

        const line = new THREE.LineSegments(geometry, material);
        line.frustumCulled = false;
        this.scene.add(line);
        DrawingGlobals.lineMeshes.push(line);
    }

    // Update the end of the most recent line
    updateLine(point) {
        if (!DrawingGlobals.lineMeshes.length) return;
        const lastLine = DrawingGlobals.lineMeshes.slice(-1)[0];
        const positionArray = lastLine.geometry.attributes.position.array;
        positionArray[3] = point.x;
        positionArray[4] = point.y;
        positionArray[5] = point.z;
        lastLine.geometry.attributes.position.needsUpdate = true;
    }

    removeLines() {
        removeLineMeshes(this.scene);
    }

    completeShape() {
        LABELS.angle.visible = false;
        LABELS.length.visible = false;
        hideSmartGuides();
        this.removeLines();

        this.addShape(DrawingGlobals.intersectionPoints);
        selectSegment(this.selectedIndices, POLYGONS.length - 1, this.setSelectedIndices, this.setPolygonDetails);
        this.stopDrawing();
        
    }

    addShape(coordinates) {
        const v = coordinates.map(cord => new THREE.Vector3(cord.x, cord.y, cord.z));
        let polygonMesh = earClipping(v);
        polygonMesh.renderOrder = 1;

        const createdVertexMeshes = [];
        for (let i of coordinates) {
            let vertex = createVertex(i);
            DrawingGlobals.verticesMesh.push(vertex);
            createdVertexMeshes.push(vertex);
            polygonMesh.add(vertex);
        }

        const { edgeData, edgeWireframe, setBackData } = createEdges(v, this.scene);
        let setBacks = { isSet: false, edgesData: setBackData, avaArea: 0 };

        const polygonCenter = getGeoCenter(polygonMesh);
        const segmentLabel = createNameLabel(polygonCenter);

        polygonMesh.add(edgeWireframe);
        this.scene.add(polygonMesh);
        this.scene.add(segmentLabel);

        let details = this.getDetails(edgeData);
        setBacks.avaArea = details.area;

        // ✅ stable per-segment id (only used for irradiance caching/jobs)
const segmentId =
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `seg_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const segmentData = {
  id: segmentId, // ✅ added (doesn't affect existing index-based logic)
  shape: polygonMesh,
  edges: edgeData,
  label: segmentLabel,
  obstructions: [],
  panels: [],
  details: details,
  setBacks: setBacks,

  // ✅ added: per-segment irradiance cache storage
  irradiance: {
    tof: null,
    gti: null,
    asa: null,
    monthlyASA: null,
    irradiancePoints: null,
    status: {
      tof: "idle", // idle | pending | done | error
      gti: "idle",
      asa: "idle",
    },
    error: null,
  },
};



        POLYGONS.push(segmentData);

        const newSegmentIndex = POLYGONS.length - 1;

        let uiData = {
            label: segmentLabel.element.innerText,
            details: details,
            setbacks: setBacks.isSet,
            obstructions: [],
            panels: []
        };

        console.log("AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA1");
        console.log("My scene in SegmentManager:", this.scene);

        this.updateSegmentList((oldPolygons) => [ ...(oldPolygons || []), uiData ]);

        // snapshot of initial coordinates for redo
        const coordinatesSnapshot = v.map(p => p.clone());

        // vertex groups: drawing array clean-up will push created vertex meshes into VERTICES
        pushUndoAction({
            label: "Add segment",
            undo: () => {
                if (!POLYGONS[newSegmentIndex]) return;
                removeSegmentDragControls();
                deleteSingleSegment(newSegmentIndex);

                this.updateSegmentList((oldPolygonsInner) => {
                    if (!oldPolygonsInner) return oldPolygonsInner;
                    return oldPolygonsInner.filter((_, idx) => idx !== newSegmentIndex);
                });

                if (typeof this.setSelectedIndices === "function") {
                    this.setSelectedIndices((prev) => {
                        if (!prev || prev.segmentIndex === null) return prev;
                        if (prev.segmentIndex === newSegmentIndex) {
                            return {
                                segmentIndex: null,
                                obstacleIndex: null,
                                panelIndex: null,
                            };
                        }
                        if (prev.segmentIndex > newSegmentIndex) {
                            return {
                                ...prev,
                                segmentIndex: prev.segmentIndex - 1,
                            };
                        }
                        return prev;
                    });
                }
            },
            redo: () => {
                // If segment already exists at that index, don't double-create
                if (POLYGONS[newSegmentIndex]) return;

                const scene = this.scene;

                const vRestored = coordinatesSnapshot.map(p => p.clone());
                let polygonMeshR = earClipping(vRestored);
                polygonMeshR.renderOrder = 1;

                const vertexMeshesR = [];
                for (let p of vRestored) {
                    const vertex = createVertex(p);
                    vertexMeshesR.push(vertex);
                    polygonMeshR.add(vertex);
                }

                const { edgeData: edgeDataR, edgeWireframe: edgeWireframeR, setBackData: setBackDataR } =
                    createEdges(vRestored, scene);

                const setBacksR = {
                    isSet: false,
                    edgesData: setBackDataR,
                    avaArea: 0,
                };

                const polygonCenterR = getGeoCenter(polygonMeshR);
                const segmentLabelR = createNameLabel(polygonCenterR);

                polygonMeshR.add(edgeWireframeR);
                scene.add(polygonMeshR);
                scene.add(segmentLabelR);

                const detailsR = this.getDetails(edgeDataR);
                setBacksR.avaArea = detailsR.area;

                const segmentDataR = {
                    shape: polygonMeshR,
                    edges: edgeDataR,
                    label: segmentLabelR,
                    obstructions: [],
                    panels: [],
                    details: detailsR,
                    setBacks: setBacksR,
                };

                // Insert back into POLYGONS & VERTICES at original index
                POLYGONS.splice(newSegmentIndex, 0, segmentDataR);
                VERTICES.splice(newSegmentIndex, 0, vertexMeshesR);

                // UI list insertion
                this.updateSegmentList((prev) => {
                    if (!prev) return prev;
                    const uiDataR = {
                        label: segmentLabelR.element.innerText,
                        details: detailsR,
                        setbacks: setBacksR.isSet,
                        obstructions: [],
                        panels: [],
                    };
                    const arr = [...prev];
                    arr.splice(newSegmentIndex, 0, uiDataR);
                    return arr;
                });

                if (typeof this.setSelectedIndices === "function") {
                    this.setSelectedIndices({
                        segmentIndex: newSegmentIndex,
                        obstacleIndex: null,
                        panelIndex: null,
                    });
                }
            }
        });

        this.cleanDrawingArray();
    }

    getDetails(edges) {
        let res = calculatePitch(edges);
        let pitch = res.pitch_angle;
        let area = getPolyArea(edges).toFixed(2);
        let azimu = getAzimuth(res.p2, res.p1).toFixed(2);
        return { pitch, area, azimu };
    }

    cleanDrawingArray() {
        VERTICES.push([...DrawingGlobals.verticesMesh]);
        DrawingGlobals.intersectionPoints = [];
        DrawingGlobals.verticesMesh = [];
        DrawingGlobals.lineMeshes = [];
        DrawingGlobals.lengthSnapMatchedEdges = [];
    }

    // local drawing-time undo (not global UndoManager)
    undo() {
        LINES.referenceLine.visible = false;
        LABELS.angle.visible = false;

        let point, lastLine, lastVertex = null;

        if (DrawingGlobals.intersectionPoints.length === 1) {
            HISTORY.length = 0;
            this.lastUndo = true;
        }

        if (DrawingGlobals.intersectionPoints.length > 0) {
            point = DrawingGlobals.intersectionPoints.pop();
        }

        if (DrawingGlobals.lineMeshes.length > 0) {
            lastLine = DrawingGlobals.lineMeshes.pop();
            this.scene.remove(lastLine);
            lastLine.geometry.dispose();
        }

        if (DrawingGlobals.verticesMesh.length > 0) {
            lastVertex = DrawingGlobals.verticesMesh.pop();
            this.scene.remove(lastVertex);
        }

        if (DrawingGlobals.intersectionPoints.length === 0) {
            HISTORY.length = 0;
            this.stopDrawing();
            hideSmartGuides();
            DrawingGlobals.lengthSnapMatchedEdges = [];
        } else if ((point || lastLine || lastVertex) && !this.lastUndo) {
            this.updateLine(this.lastMouseIntersect);

            HISTORY.push({
                point,
                lastLine,
                lastVertex,
                lastPoint: this.lastMouseIntersect
            });
        }
    }

    // local drawing-time redo (uses HISTORY)
    redo() {
        if (HISTORY.length > 0) {
            let redoThis = HISTORY.pop();

            if (redoThis.point) {
                DrawingGlobals.intersectionPoints.push(redoThis.point);
            }

            if (redoThis.lastLine) {
                this.updateLine(DrawingGlobals.intersectionPoints.at(-1));
                DrawingGlobals.lineMeshes.push(redoThis.lastLine);
                this.scene.add(redoThis.lastLine);
            }

            this.updateLine(this.lastMouseIntersect);
        }
    }
}

export default SegmentManager;
